import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Brands() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/content/brands`)
      .then((r) => setBrands(r.data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <main data-testid="brands-page" className="min-h-screen bg-[#050505]">
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Link
            to="/"
            data-testid="brands-back-home"
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft size={14} />
            Back
          </Link>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#E63946] font-bold">
            Partners & Collaborators
          </span>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl md:text-6xl font-black text-white tracking-tighter">
            Brand Partnerships
          </h1>
          <p className="mt-4 text-sm md:text-base text-zinc-400 max-w-2xl leading-relaxed">
            Every brand George has collaborated with — from global tech giants to premium lifestyle labels. Tap any brand to visit their site.
          </p>
          {!loading && (
            <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03]">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-semibold">
                {brands.length} {brands.length === 1 ? "Brand" : "Brands"}
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Grid */}
      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {loading ? (
            <p className="text-center text-zinc-500 text-sm">Loading brands…</p>
          ) : brands.length === 0 ? (
            <p className="text-center text-zinc-500 text-sm">No brands yet.</p>
          ) : (
            <div
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-5"
              data-testid="brands-grid"
            >
              {brands.map((brand, i) => {
                const Card = ({ children }) => (
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-30px" }}
                    transition={{ delay: Math.min(i, 20) * 0.03, duration: 0.4 }}
                    whileHover={{ y: -4 }}
                    className="group relative rounded-2xl bg-gradient-to-br from-[#0F0F11] to-[#080809] border border-white/5 hover:border-[#E63946]/40 transition-all duration-500 overflow-hidden"
                  >
                    {children}
                  </motion.div>
                );

                const Content = (
                  <>
                    <div className="absolute -top-10 -right-10 w-28 h-28 bg-[#E63946]/8 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                    <div className="relative p-5 md:p-6 flex flex-col items-center text-center">
                      {/* Logo */}
                      <div className="w-full aspect-[2/1] flex items-center justify-center mb-4">
                        {brand.logo ? (
                          <img
                            src={brand.logo}
                            alt={brand.name}
                            className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-xl bg-[#E63946]/10 border border-[#E63946]/30 flex items-center justify-center">
                            <span className="font-heading text-xl font-bold text-[#E63946]">
                              {brand.name?.charAt(0) || "?"}
                            </span>
                          </div>
                        )}
                      </div>
                      {/* Name */}
                      <h3
                        className="font-heading text-sm md:text-base font-bold text-white leading-tight group-hover:text-[#E63946] transition-colors duration-300"
                        data-testid={`brand-name-${brand.id}`}
                      >
                        {brand.name}
                      </h3>
                      {brand.link && (
                        <span className="mt-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500 group-hover:text-[#E63946] transition-colors">
                          Visit <ExternalLink size={10} />
                        </span>
                      )}
                    </div>
                  </>
                );

                if (brand.link) {
                  return (
                    <a
                      key={brand.id}
                      href={brand.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`brand-card-${brand.id}`}
                      className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E63946] rounded-2xl"
                    >
                      <Card>{Content}</Card>
                    </a>
                  );
                }
                return (
                  <div key={brand.id} data-testid={`brand-card-${brand.id}`}>
                    <Card>{Content}</Card>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
